import { cn } from '../../lib/utils'
import type { ReactNode } from 'react'

type BadgeVariant = 'blue' | 'green' | 'yellow' | 'red' | 'gray' | 'orange' | 'purple'

interface BadgeProps {
  children: ReactNode
  variant?: BadgeVariant
  className?: string
  dot?: boolean
}

const variants: Record<BadgeVariant, string> = {
  blue: 'bg-blue-50 text-blue-700',
  green: 'bg-emerald-50 text-emerald-700',
  yellow: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
  orange: 'bg-orange-50 text-orange-700',
  purple: 'bg-purple-50 text-purple-700',
}

const dotColors: Record<BadgeVariant, string> = {
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
  orange: 'bg-orange-500',
  purple: 'bg-purple-500',
}

export function Badge({ children, variant = 'gray', className, dot }: BadgeProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', variants[variant], className)}>
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full', dotColors[variant])} />}
      {children}
    </span>
  )
}

// Convenience mappers
export function besuchStatusBadge(status: string) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    geplant: { variant: 'blue', label: 'Geplant' },
    unterwegs: { variant: 'orange', label: 'Unterwegs' },
    abgeschlossen: { variant: 'green', label: 'Abgeschlossen' },
    abgesagt: { variant: 'gray', label: 'Abgesagt' },
  }
  const m = map[status] ?? { variant: 'gray', label: status }
  return <Badge variant={m.variant} dot>{m.label}</Badge>
}

export function defektZustandBadge(zustand: string) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    leicht: { variant: 'yellow', label: 'Leicht' },
    mittel: { variant: 'orange', label: 'Mittel' },
    schwer: { variant: 'red', label: 'Schwer' },
    'total-ausfall': { variant: 'red', label: 'Totalausfall' },
  }
  const m = map[zustand] ?? { variant: 'gray', label: zustand }
  return <Badge variant={m.variant}>{m.label}</Badge>
}

export function defektStatusBadge(status: string) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    offen: { variant: 'red', label: 'Offen' },
    'in-bearbeitung': { variant: 'yellow', label: 'In Bearbeitung' },
    behoben: { variant: 'green', label: 'Behoben' },
  }
  const m = map[status] ?? { variant: 'gray', label: status }
  return <Badge variant={m.variant} dot>{m.label}</Badge>
}

export function geraetStatusBadge(status: string) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    aktiv: { variant: 'green', label: 'Aktiv' },
    defekt: { variant: 'red', label: 'Defekt' },
    ersetzt: { variant: 'gray', label: 'Ersetzt' },
    entfernt: { variant: 'gray', label: 'Entfernt' },
  }
  const m = map[status] ?? { variant: 'gray', label: status }
  return <Badge variant={m.variant} dot>{m.label}</Badge>
}

export function offertStatusBadge(status: string) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    entwurf: { variant: 'gray', label: 'Entwurf' },
    gesendet: { variant: 'blue', label: 'Gesendet' },
    angenommen: { variant: 'green', label: 'Angenommen' },
    abgelehnt: { variant: 'red', label: 'Abgelehnt' },
    abgelaufen: { variant: 'orange', label: 'Abgelaufen' },
  }
  const m = map[status] ?? { variant: 'gray', label: status }
  return <Badge variant={m.variant}>{m.label}</Badge>
}
